import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Delete } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import SingleSelect from "@/components/controls/SingleSelect";
import Text from "@/components/ui/text";
import { useServiceItemTypes } from "@/pages/settings/pages/products-v2/hooks";
import {
  BillingFrequency,
  ServiceDuration
} from "@/pages/settings/pages/products-v2/types";
import {
  formatCurrency,
  formatQuantityInput,
  getCurrencySymbol,
  getSymbolAdornmentPadding,
  isValidQuantity,
  parseQuantityInput
} from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import {
  buildBillingFrequencyOptions,
  buildDurationOptions,
  buildTermOptions,
  getLineFieldVisibility,
  getLineTotal,
  hasSelectedTerm,
  isFlatRateService,
  isFlexibleService
} from "./financials";
import FieldLabel from "./FieldLabel";
import { DealServiceLine } from "./types";

interface Props {
  line: DealServiceLine;
  currency: string;
  isExpanded: boolean;
  onToggle: () => void;
  onChange: (changes: Partial<DealServiceLine>) => void;
  onRemove: () => void;
}

const DealServiceRow: React.FC<Props> = ({
  line,
  currency,
  isExpanded,
  onToggle,
  onChange,
  onRemove
}) => {
  const { t } = useTranslation();
  const { data: serviceItemTypes = [] } = useServiceItemTypes();
  // The line keeps the parsed number; the text the user is part-way through
  // typing ("-", "0.") only has to survive within the input itself.
  const [quantityInput, setQuantityInput] = useState<string>(() =>
    formatQuantityInput(line.quantity)
  );

  const typeName = serviceItemTypes.find(
    (type) => type.id === line.service.typeId
  )?.name;

  const quantityInputId = `service-quantity-${line.key}`;
  const priceInputId = `service-price-${line.key}`;
  const visibility = getLineFieldVisibility(line);
  const total = getLineTotal(line);
  const currencySymbol = getCurrencySymbol(currency);
  const priceLabelKey = isFlexibleService(line.service)
    ? "deals.createModal.services.price"
    : isFlatRateService(line.service)
      ? "deals.createModal.services.priceEntirePeriod"
      : "deals.createModal.services.pricePerUnit";

  return (
    <div
      className="flex flex-col rounded-xl border-[0.5px] border-border-border1 bg-surface-elevation2 overflow-hidden"
      data-test={`create-deal-service-${line.service.id}`}
    >
      <div className="flex items-center gap-2 p-3">
        <IconButton
          variant="iconNoBg"
          size="sm"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-label={line.service.name}
        >
          {isExpanded ? <ChevronDown /> : <ChevronRight />}
        </IconButton>
        <Text variant="body">{line.service.name}</Text>
        {typeName ? <Text variant="bodyTertiary">{typeName}</Text> : null}
        <div className="ml-auto flex items-center gap-2">
          <Text variant="body">{formatCurrency(total, currency)}</Text>
          <IconButton
            variant="icon"
            size="md"
            onClick={onRemove}
            tooltipText={t("deals.createModal.services.removeService")}
            data-test="create-deal-service-remove"
          >
            <Delete />
          </IconButton>
        </div>
      </div>

      {isExpanded ? (
        <div className="flex flex-col gap-4 border-t-[0.5px] border-border-border1 p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <FieldLabel label={t("deals.createModal.services.terms")} />
              <SingleSelect
                fieldName={`service-terms-${line.key}`}
                options={buildTermOptions(line.service, t, currency)}
                selectedValue={line.pricingId}
                onSelection={(value) => onChange({ pricingId: String(value) })}
                variant="dropdown"
                className="w-full"
                hasError={!hasSelectedTerm(line)}
              />
              {!hasSelectedTerm(line) ? (
                <Text variant="smBodyError">
                  {t("deals.createModal.services.termsRequired")}
                </Text>
              ) : null}
            </div>

            {visibility.quantity ? (
              <div className="flex flex-col gap-1">
                <FieldLabel
                  label={t("contractTerms.quantity")}
                  htmlFor={quantityInputId}
                />
                <Input
                  id={quantityInputId}
                  type="text"
                  inputMode="decimal"
                  value={quantityInput}
                  onChange={(event) => {
                    setQuantityInput(event.target.value);
                    onChange({
                      quantity: parseQuantityInput(event.target.value)
                    });
                  }}
                  hasError={!isValidQuantity(line.quantity)}
                  className="w-full"
                />
                {!isValidQuantity(line.quantity) ? (
                  <Text variant="smBodyError">
                    {t("contractTerms.quantityMinError")}
                  </Text>
                ) : null}
              </div>
            ) : null}
          </div>

          {visibility.duration ||
          visibility.billingFrequency ||
          visibility.price ? (
            <div className="grid grid-cols-2 gap-4">
              {visibility.duration ? (
                <div className="flex flex-col gap-1">
                  <FieldLabel label={t("contractTerms.duration")} />
                  <SingleSelect
                    fieldName={`service-duration-${line.key}`}
                    options={buildDurationOptions(t)}
                    selectedValue={line.duration ?? undefined}
                    onSelection={(value) =>
                      onChange({ duration: Number(value) as ServiceDuration })
                    }
                    variant="dropdown"
                    className="w-full"
                    hasError={line.duration == null}
                  />
                  {line.duration == null ? (
                    <Text variant="smBodyError">
                      {t("deals.createModal.services.durationRequired")}
                    </Text>
                  ) : null}
                </div>
              ) : null}

              {visibility.billingFrequency ? (
                <div className="flex flex-col gap-1">
                  <FieldLabel label={t("contractTerms.billingFrequency")} />
                  <SingleSelect
                    fieldName={`service-billing-frequency-${line.key}`}
                    options={buildBillingFrequencyOptions(t)}
                    selectedValue={line.billingFrequency ?? undefined}
                    onSelection={(value) =>
                      onChange({
                        billingFrequency: Number(value) as BillingFrequency
                      })
                    }
                    variant="dropdown"
                    className="w-full"
                    hasError={line.billingFrequency == null}
                  />
                  {line.billingFrequency == null ? (
                    <Text variant="smBodyError">
                      {t("deals.createModal.services.billingFrequencyRequired")}
                    </Text>
                  ) : null}
                </div>
              ) : null}

              {visibility.price ? (
                <div className="flex flex-col gap-1">
                  <FieldLabel label={t(priceLabelKey)} htmlFor={priceInputId} />
                  <div className="relative w-full">
                    <Input
                      id={priceInputId}
                      type="number"
                      min={0}
                      step="any"
                      value={line.price || ""}
                      placeholder={t(
                        "deals.createModal.services.pricePlaceholder"
                      )}
                      onChange={(event) =>
                        onChange({
                          price: Math.max(
                            0,
                            parseFloat(event.target.value) || 0
                          )
                        })
                      }
                      hasError={!(line.price > 0)}
                      className="w-full"
                      style={{
                        paddingLeft: getSymbolAdornmentPadding(currencySymbol)
                      }}
                    />
                    <Text
                      as="span"
                      variant="bodySecondary"
                      className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    >
                      {currencySymbol}
                    </Text>
                  </div>
                  {!(line.price > 0) ? (
                    <Text variant="smBodyError">
                      {t("deals.createModal.services.priceRequired")}
                    </Text>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default DealServiceRow;
